/**
 * API Ministry Service Implementation
 * 
 * This service provides the API implementation for ministry operations,
 * communicating with the backend ministry endpoints to manage ministries,
 * members, activities, and metrics.
 * 
 * @module ApiMinistryService
 * @implements {IMinistryService}
 * 
 * Service Description:
 * The ApiMinistryService handles all ministry-related API calls including:
 * - Ministry CRUD operations
 * - Member management within ministries
 * - Activity tracking and attendance
 * - Ministry metrics and reporting
 * - Bulk operations for efficiency
 * 
 * @requires ApiClient for HTTP requests
 * @requires IMinistryService interface
 */

import { AxiosInstance } from 'axios';
import { 
  IMinistryService 
} from '../../interfaces/IMinistryService';
import { 
  MinistryActivity, 
  MinistryMember, 
  MinistryMetrics,
  EnhancedMinistry,
  CreateActivityRequest,
  UpdateActivityRequest,
  AddMemberRequest,
  MinistryReportSchedule
} from '../../../types/ministry';
import { ScheduledReport } from '../../../types/reporting/schedule';
import { PaginatedResponse } from '../../interfaces/IDataSource';
import { BaseService } from '../base/BaseService';
import ApiClient from '../../../utils/apiClient';

export class ApiMinistryService extends BaseService implements IMinistryService {
  private api: AxiosInstance;
  private baseURL: string = '/api/v1/church/';

  constructor() {
    super('ApiMinistryService');
    this.api = ApiClient.getInstance();
    console.log('[ApiMinistryService] Initialized with base URL:', this.baseURL);
  }

  // ==================== Ministry Operations ====================
  
  async getMinistry(id: string): Promise<EnhancedMinistry> {
    try {
      console.log(`[ApiMinistryService] Fetching ministry ${id}`);
      
      // Get church ID from the ministry first
      const ministryResponse = await this.api.get(`${this.baseURL}ministries/${id}/`);
      const churchId = ministryResponse.data.church;
      
      // Then fetch with proper URL structure
      const response = await this.api.get(`${this.baseURL}${churchId}/ministries/${id}/`);
      
      return this.transformMinistryFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `fetching ministry ${id}`);
    }
  }

  async getMinistries(churchId: string): Promise<EnhancedMinistry[]> {
    try {
      console.log(`[ApiMinistryService] Fetching ministries for church ${churchId}`);
      
      const response = await this.api.get(`${this.baseURL}${churchId}/ministries/`);
      
      const ministries = response.data.results || response.data;
      return ministries.map((ministry: any) => 
        this.transformMinistryFromAPI(ministry)
      );
    } catch (error) {
      this.handleError(error, 'fetching ministries');
    }
  }

  async createMinistry(
    ministry: Omit<EnhancedMinistry, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<EnhancedMinistry> {
    try {
      console.log('[ApiMinistryService] Creating ministry:', ministry.name);
      
      const apiData = this.transformMinistryForAPI(ministry);
      
      const response = await this.api.post(
        `${this.baseURL}${ministry.churchId}/ministries/`, 
        apiData
      );
      
      console.log('[ApiMinistryService] Ministry created:', response.data.id);
      return this.transformMinistryFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'creating ministry');
    }
  }

  async updateMinistry(id: string, updates: Partial<EnhancedMinistry>): Promise<EnhancedMinistry> {
    try {
      console.log(`[ApiMinistryService] Updating ministry ${id}`);
      
      // Get church ID first
      const ministryResponse = await this.api.get(`${this.baseURL}ministries/${id}/`);
      const churchId = ministryResponse.data.church;
      
      const apiData = this.transformMinistryForAPI(updates);
      
      const response = await this.api.patch(
        `${this.baseURL}${churchId}/ministries/${id}/`, 
        apiData
      );
      
      console.log(`[ApiMinistryService] Ministry ${id} updated`);
      return this.transformMinistryFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `updating ministry ${id}`);
    }
  }

  async deleteMinistry(id: string): Promise<void> {
    try {
      console.log(`[ApiMinistryService] Deleting ministry ${id}`);
      
      // Get church ID first
      const ministryResponse = await this.api.get(`${this.baseURL}ministries/${id}/`);
      const churchId = ministryResponse.data.church;
      
      await this.api.delete(`${this.baseURL}${churchId}/ministries/${id}/`);
      
      console.log(`[ApiMinistryService] Ministry ${id} deleted successfully`);
    } catch (error) {
      this.handleError(error, `deleting ministry ${id}`);
    }
  }

  // ==================== Activity Management ====================
  
  async getMinistryActivities(
    ministryId: string, 
    page: number = 1, 
    limit: number = 25
  ): Promise<PaginatedResponse<MinistryActivity>> {
    try {
      console.log(`[ApiMinistryService] Fetching activities for ministry ${ministryId}`);
      
      // Note: Activity endpoints may not exist in backend yet
      // This is a placeholder implementation
      console.warn('[ApiMinistryService] Activity tracking not yet implemented in backend');
      
      return {
        results: [],
        count: 0,
        next: null,
        previous: null
      };
    } catch (error) {
      this.handleError(error, 'fetching ministry activities');
    }
  }

  async getActivity(activityId: string): Promise<MinistryActivity> {
    // Placeholder - backend doesn't have activity tracking yet
    throw new Error('Activity tracking not yet implemented in backend');
  }

  async createActivity(activity: CreateActivityRequest): Promise<MinistryActivity> {
    // Placeholder - backend doesn't have activity tracking yet
    throw new Error('Activity tracking not yet implemented in backend');
  }

  async updateActivity(id: string, updates: UpdateActivityRequest): Promise<MinistryActivity> {
    // Placeholder - backend doesn't have activity tracking yet
    throw new Error('Activity tracking not yet implemented in backend');
  }

  async deleteActivity(id: string): Promise<void> {
    // Placeholder - backend doesn't have activity tracking yet
    throw new Error('Activity tracking not yet implemented in backend');
  }

  async recordAttendance(
    activityId: string, 
    attendanceCount: number, 
    participantIds: string[]
  ): Promise<MinistryActivity> {
    // Placeholder - backend doesn't have activity tracking yet
    throw new Error('Activity tracking not yet implemented in backend');
  }

  // ==================== Member Management ====================
  
  async getMinistryMembers(ministryId: string): Promise<MinistryMember[]> {
    try {
      console.log(`[ApiMinistryService] Fetching members for ministry ${ministryId}`);
      
      // Get church ID first
      const ministryResponse = await this.api.get(`${this.baseURL}ministries/${ministryId}/`);
      const churchId = ministryResponse.data.church;
      
      // Note: This endpoint needs to be added to the backend
      const response = await this.api.get(
        `${this.baseURL}${churchId}/ministries/${ministryId}/members/`
      );
      
      const members = response.data.results || response.data;
      return members.map((member: any) =>
        this.transformMemberFromAPI(member)
      );
    } catch (error: any) {
      // If endpoint doesn't exist, throw detailed error
      if (error.response?.status === 404) {
        const errorDetails = {
          message: 'Ministry members endpoint not implemented',
          component: 'ApiMinistryService',
          method: 'getMinistryMembers',
          ministryId: ministryId,
          endpoint: `${this.baseURL}{churchId}/ministries/${ministryId}/members/`,
          status: 404,
          timestamp: new Date().toISOString(),
          hint: 'Backend endpoint needs to be implemented'
        };
        console.error('[ApiMinistryService] Endpoint not found:', errorDetails);
        throw new Error(JSON.stringify(errorDetails, null, 2));
      }
      this.handleError(error, 'fetching ministry members');
    }
  }

  async addMinistryMember(member: AddMemberRequest): Promise<MinistryMember> {
    try {
      console.log('[ApiMinistryService] Adding member to ministry');
      
      // Get church ID first
      const ministryResponse = await this.api.get(`${this.baseURL}ministries/${member.ministryId}/`);
      const churchId = ministryResponse.data.church;
      
      const response = await this.api.post(
        `${this.baseURL}${churchId}/ministries/${member.ministryId}/add_member/`,
        {
          user_id: member.userId,
          role: member.role
        }
      );
      
      return this.transformMemberFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'adding ministry member');
    }
  }

  async updateMemberRole(memberId: string, role: MinistryMember['role']): Promise<MinistryMember> {
    try {
      console.log(`[ApiMinistryService] Updating member ${memberId} role to ${role}`);
      
      // This endpoint may need to be implemented in backend
      throw new Error('Update member role not yet implemented in backend');
    } catch (error) {
      this.handleError(error, 'updating member role');
    }
  }

  async removeMinistryMember(memberId: string): Promise<void> {
    try {
      console.log(`[ApiMinistryService] Removing member ${memberId}`);
      
      // This endpoint may need to be implemented in backend
      throw new Error('Remove member not yet implemented in backend');
    } catch (error) {
      this.handleError(error, 'removing ministry member');
    }
  }

  async getMemberMinistries(userId: string): Promise<EnhancedMinistry[]> {
    const errorDetails = {
      message: 'Get member ministries not yet implemented',
      component: 'ApiMinistryService',
      method: 'getMemberMinistries',
      userId: userId,
      timestamp: new Date().toISOString(),
      hint: 'Backend endpoint for user ministry listing needs to be implemented'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  // ==================== Metrics and Reporting ====================
  
  async getMinistryMetrics(ministryId: string): Promise<MinistryMetrics> {
    try {
      console.log(`[ApiMinistryService] Fetching metrics for ministry ${ministryId}`);
      
      // Calculate basic metrics from available data
      const ministry = await this.getMinistry(ministryId);
      const members = await this.getMinistryMembers(ministryId);
      
      return {
        id: `${ministryId}-metrics`,
        ministryId,
        totalActivities: 0, // Not tracked yet
        activitiesThisMonth: 0,
        totalMembers: members.length,
        activeMembers: members.filter(m => m.isActive).length,
        averageAttendance: 0,
        memberGrowth: 0,
        engagementScore: 75, // Mock value
        lastActivityDate: new Date().toISOString(),
        topActivities: [],
        attendanceTrend: []
      };
    } catch (error) {
      this.handleError(error, 'fetching ministry metrics');
    }
  }

  async getMinistryMetricsTrend(
    ministryId: string,
    period: 'week' | 'month' | 'quarter' | 'year'
  ): Promise<MinistryMetrics[]> {
    const errorDetails = {
      message: 'Ministry metrics trends not yet implemented',
      component: 'ApiMinistryService',
      method: 'getMinistryMetricsTrend',
      ministryId: ministryId,
      period: period,
      timestamp: new Date().toISOString(),
      hint: 'Backend needs to implement metrics history tracking over time'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  async scheduleMinistryReport(schedule: MinistryReportSchedule): Promise<ScheduledReport> {
    // Placeholder - would integrate with reporting service
    throw new Error('Ministry report scheduling not yet implemented');
  }

  async getMinistryScheduledReports(ministryId: string): Promise<ScheduledReport[]> {
    const errorDetails = {
      message: 'Ministry scheduled reports not yet implemented',
      component: 'ApiMinistryService',
      method: 'getMinistryScheduledReports',
      ministryId: ministryId,
      timestamp: new Date().toISOString(),
      hint: 'Needs integration with reporting service for scheduled report retrieval'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  // ==================== Bulk Operations ====================
  
  async bulkAddMembers(ministryId: string, members: AddMemberRequest[]): Promise<MinistryMember[]> {
    try {
      console.log(`[ApiMinistryService] Bulk adding ${members.length} members`);
      
      // Would need backend endpoint for bulk operations
      // For now, add one by one
      const results: MinistryMember[] = [];
      
      for (const member of members) {
        try {
          const added = await this.addMinistryMember(member);
          results.push(added);
        } catch (error) {
          console.error(`Failed to add member ${member.userId}:`, error);
        }
      }
      
      return results;
    } catch (error) {
      this.handleError(error, 'bulk adding members');
    }
  }

  async bulkRemoveMembers(ministryId: string, memberIds: string[]): Promise<void> {
    // Placeholder - bulk operations not implemented
    throw new Error('Bulk remove not yet implemented');
  }

  // ==================== Search and Filtering ====================
  
  async searchActivities(ministryId: string, query: string): Promise<MinistryActivity[]> {
    const errorDetails = {
      message: 'Ministry activity search not yet implemented',
      component: 'ApiMinistryService',
      method: 'searchActivities',
      ministryId: ministryId,
      query: query,
      timestamp: new Date().toISOString(),
      hint: 'Backend needs activity search endpoint implementation'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  async getUpcomingActivities(ministryId: string, days: number): Promise<MinistryActivity[]> {
    const errorDetails = {
      message: 'Get upcoming activities not yet implemented',
      component: 'ApiMinistryService',
      method: 'getUpcomingActivities',
      ministryId: ministryId,
      days: days,
      timestamp: new Date().toISOString(),
      hint: 'Backend needs activity filtering by future dates'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  async getPastActivities(
    ministryId: string,
    page: number = 1,
    limit: number = 25
  ): Promise<PaginatedResponse<MinistryActivity>> {
    const errorDetails = {
      message: 'Get past activities not yet implemented',
      component: 'ApiMinistryService',
      method: 'getPastActivities',
      ministryId: ministryId,
      page: page,
      limit: limit,
      timestamp: new Date().toISOString(),
      hint: 'Backend needs activity filtering by past dates with pagination'
    };
    console.error('[ApiMinistryService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  // ==================== Private Helper Methods ====================
  
  private transformMinistryFromAPI(data: any): EnhancedMinistry {
    return {
      id: data.id,
      churchId: data.church,
      name: data.name,
      description: data.description || '',
      leaderId: data.leader || data.member_leader || '',
      memberCount: data.member_count || 0,
      isActive: data.is_active || true,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      // Enhanced fields
      activities: [],
      members: [],
      metrics: undefined,
      meetingSchedule: undefined,
      contactEmail: data.email,
      contactPhone: data.phone
    };
  }

  private transformMinistryForAPI(ministry: Partial<EnhancedMinistry>): any {
    const apiData: any = {};
    
    if (ministry.name !== undefined) apiData.name = ministry.name;
    if (ministry.description !== undefined) apiData.description = ministry.description;
    if (ministry.leaderId !== undefined) apiData.leader = ministry.leaderId;
    if (ministry.isActive !== undefined) apiData.is_active = ministry.isActive;
    if (ministry.contactEmail !== undefined) apiData.email = ministry.contactEmail;
    if (ministry.contactPhone !== undefined) apiData.phone = ministry.contactPhone;
    
    return apiData;
  }

  private transformMemberFromAPI(data: any): MinistryMember {
    return {
      id: data.id,
      userId: data.user_id || data.user,
      ministryId: data.ministry_id || data.ministry,
      role: data.role || 'member',
      joinedAt: data.joined_at || data.created_at,
      isActive: data.is_active !== undefined ? data.is_active : true,
      user: data.user_details
    };
  }

  private handleError(error: any, operation: string): never {
    console.error(`[ApiMinistryService] Error during ${operation}:`, error);
    
    // Enhanced error handling
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || error.message;
      
      switch (status) {
        case 400:
          throw new Error(`Invalid request: ${message}`);
        case 401:
          throw new Error('Authentication required');
        case 403:
          throw new Error('You do not have permission to perform this action');
        case 404:
          throw new Error('Ministry not found');
        case 500:
          throw new Error('Server error. Please try again later');
        default:
          throw new Error(message || 'An unexpected error occurred');
      }
    }
    
    // Network or other errors
    throw new Error(error.message || 'Network error. Please check your connection');
  }
}